import { shellCopy } from '@/copy'
import { contrasts } from '@/styles/axes'
import { Avatar } from '@/ui/Avatar/Avatar'
import { PaletteSwatch, RailButton, RailDivided } from '@/ui/Console/Console'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Menu, type MenuEntry } from '@/ui/Menu/Menu'
import { Popover } from '@/ui/Popover/Popover'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { SegmentedControl } from '@/ui/SegmentedControl/SegmentedControl'
import { ROUTES } from '../../constants/routes'
import { useFeatures } from '../../hooks/queries'
import {
  isDarkTheme,
  PALETTES,
  useAppearanceStore,
  useContrastStore,
} from '../../store/appearance'
import { useAuthStore } from '../../store/auth'
import { TextSizePicker } from '../TextSizePicker'

export function RailFooter({
  currentUser,
  push,
}: {
  currentUser: string | null
  push?: boolean
}) {
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin)
  const logout = useAuthStore((s) => s.logout)
  const features = useFeatures()
  const { themeId, setTheme } = useAppearanceStore()
  const dark = isDarkTheme(themeId)

  const accountItems: MenuEntry[] = [
    {
      id: 'settings',
      label: shellCopy.account.settings,
      href: ROUTES.settings,
    },
    ...(features.billing
      ? [
          {
            id: 'billing',
            label: shellCopy.account.billing,
            href: ROUTES.settingsBilling,
          },
        ]
      : []),
    ...(isSystemAdmin
      ? [{ id: 'admin', label: shellCopy.account.admin, href: ROUTES.admin }]
      : []),
    { type: 'separator', id: 'sep' },
    { id: 'sign-out', label: shellCopy.account.signOut, onSelect: logout },
  ]

  return (
    <RailDivided row push={push}>
      <Menu
        placement="top-start"
        trigger={
          <RailButton aria-label={shellCopy.rail.account(currentUser ?? '—')}>
            <Inline gap="space-8" wrap={false}>
              <Avatar name={currentUser ?? '?'} kind="human" size="md" />
              <Text tone="text-sub">{currentUser ?? '—'}</Text>
            </Inline>
          </RailButton>
        }
        items={accountItems}
      />
      <Popover
        placement="top-start"
        title={shellCopy.appearance.title}
        anchor={
          <IconButton ground="well" label={shellCopy.appearance.open}>
            <Icon name="half-2" />
          </IconButton>
        }
      >
        <AppearancePanel />
      </Popover>
      <IconButton
        ground="well"
        label={
          dark ? shellCopy.appearance.useLight : shellCopy.appearance.useDark
        }
        onClick={() => setTheme(dark ? 'light' : 'dark')}
      >
        <Icon name={dark ? 'sun' : 'moon'} />
      </IconButton>
    </RailDivided>
  )
}

function AppearancePanel() {
  const { themeId, paletteId, setPalette, textSizeId, setTextSize } =
    useAppearanceStore()
  const { contrastId, setContrast } = useContrastStore()
  const dark = isDarkTheme(themeId)
  return (
    <Stack gap="space-16">
      <Stack gap="space-4">
        <SectionHeader as="p">{shellCopy.appearance.colour}</SectionHeader>
        <SegmentedControl
          label={shellCopy.appearance.colour}
          value={paletteId}
          onChange={setPalette}
          options={PALETTES.map((id) => ({
            value: id,
            label: shellCopy.palettes[id],
            preview: (
              <PaletteSwatch palette={id} theme={dark ? 'dark' : 'light'} />
            ),
          }))}
        />
      </Stack>
      <Stack gap="space-4">
        <SectionHeader as="p">{shellCopy.appearance.textSize}</SectionHeader>
        <TextSizePicker value={textSizeId} onChange={setTextSize} />
      </Stack>
      <Stack gap="space-4">
        <SectionHeader as="p">{shellCopy.contrast.title}</SectionHeader>
        <SegmentedControl
          label={shellCopy.contrast.title}
          value={contrastId}
          onChange={setContrast}
          options={contrasts.map((id) => ({
            value: id,
            label: shellCopy.contrast[id],
          }))}
        />
      </Stack>
    </Stack>
  )
}
